// src/components/Seguridad/Components/Catalogos/UnidadesOrgEmpleado/UnidadesOrgEmpleadoCatalog.jsx
import { useEffect, useMemo, useState } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import { Models as ModelsGeneral } from '../../../../General/Utils/ModelsGeneral';
import { ENDPOINTS as ENDPOINTS_GENERAL } from '../../../../General/Utils/Endpoints';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './UnidadesOrgEmpleadoModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = 'Asignación Unidad Organizacional ↔ Empleado';

const endpoint = ENDPOINTS.unidadesOrgEmpleado;
const endpointById = ENDPOINTS.unidadesOrgEmpleadoById;
const modelo = Models.unidadOrgEmpleado;

export default function UnidadesOrgEmpleadoCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('new');
  const [editData, setEditData] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setFormOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const load = async () => {
    setLoading(true);
    try {
      const [res, resUnidades] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS_GENERAL.unidadesOrg),
      ]);
      const unidades = (resUnidades.data || []).map((x) => ModelsGeneral.unidadesOrg(x));

      const data = (res?.data || []).map((x) => {
        const row = modelo(x);
        const uni = unidades.find((u) => u.idUnidadOrganizacional === row.idUnidadOrganizacional);
        return {
          ...row,
          unidadNombre: uni ? uni.nombre || uni.descripcion || '' : '',
          unidadClave: uni ? uni.clave || '' : '',
        };
      });
      setRows(data);
    } catch (e) {
      console.error(e);
      triggerAlert(`Error al cargar ${nombreTabla}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try { await load(); } catch { }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => normalizeValue(JSON.stringify(r)).includes(q));
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const onNew = () => {
    setFormMode('new');
    setEditData(null);
    setFormOpen(true);
  };

  const onEdit = (row) => {
    setFormMode('edit');
    setEditData(row);
    setFormOpen(true);
  };

  const onAskDelete = (row) => {
    const id = row.idUnidadOrgEmpleado;
    const label = `UnidadOrgEmpleado #${id}`;
    setDeleteTarget({ id, name: label });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      triggerAlert('No se pudo eliminar el registro.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFromModal = async (formFromModal, modeFromModal) => {
    const mode = modeFromModal || formMode;
    try {
      setLoading(true);
      const payload = { ...(formFromModal || {}) };

      if (mode === 'new') {
        await axios.post(endpoint, payload);
      } else {
        await axios.put(endpoint, payload);
      }

      setFormOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      triggerAlert('No se pudo guardar el registro.');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {label}
    </button>
  );

  const pages = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  return (
    <div className="roles-card">
      <div className="roles-topbar">
        <h2 className="roles-title">{nombreTabla}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="roles-search"
            placeholder="Buscar…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
          <button className="btn-action" onClick={onNew}>
            ＋ Nuevo
          </button>
        </div>
      </div>

      <div className="roles-table-wrap">
        <div className="roles-table-scroller">
          <table className="roles-table">
            <thead>
              <tr>
                <th>ID Empleado</th>
                <th>Clave Unidad</th>
                <th>Unidad Organizacional</th>
                <th style={{ textAlign: 'center' }}>Editar</th>
                <th style={{ textAlign: 'center' }}>Borrar</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((row) => (
                <tr key={row.idUnidadOrgEmpleado}>
                  <td>{row.idEmpleado}</td>
                  <td>{row.unidadClave}</td>
                  <td>{row.unidadNombre || `Unidad #${row.idUnidadOrganizacional}`}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="icon-button icon-button--edit"
                      onClick={() => onEdit(row)}
                      title="Editar"
                    >
                      <img src={sigmaEdit} alt="Editar" className="sigma-icon" />
                    </button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="icon-button icon-button--delete"
                      onClick={() => onAskDelete(row)}
                      title="Borrar"
                    >
                      <img src={sigmaDelete} alt="Borrar" className="sigma-icon" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && paged.length === 0 && (
                <tr>
                  <td colSpan={4}>Sin resultados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="roles-pagination" style={{ marginTop: 8 }}>
        <div />
        <div className="roles-pagination-right">
          <PageBtn
            label="‹"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          />
          {pages.map((n) => (
            <PageBtn
              key={n}
              label={n}
              onClick={() => setPage(n)}
              active={n === page}
            />
          ))}
          <PageBtn
            label="›"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          />
        </div>
      </div>

      {formOpen && (
        <Modal
          open={formOpen}
          mode={formMode}
          data={editData}
          onClose={() => setFormOpen(false)}
          onSubmit={handleSubmitFromModal}
          inline={false}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
